import { memo, useCallback, useMemo } from 'react';
import { Badge } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { USER_TYPE_META } from '../../../constants';
import { ROW_STATUS } from '../../../constants/reviewFilters.js';
import { fileUserType } from '../../../utils/accessRules.js';
import AccessPopover from './AccessPopover.jsx';
import RoleOverflowChips from './RoleOverflowChips.jsx';

/**
 * Access cell. Existing members say "Already a member" (their access is never changed). Before
 * assignment: No access yet, the hint with what the file says and `+ Role`. After: the
 * user-type chip plus removable role and group chips.
 *
 * @param {object} props
 * @param {object} props.row
 * @param {{ roles: Array<{ id: string, name: string, studios: string[] }>, groups: Array<{ id: string, name: string }> }} props.catalog
 * @param {(rowNumber: number, access: object|null) => void} props.onChange
 */
function AccessCell({ row, catalog, onChange }) {
  const { t } = useTranslation('people-access');
  const { access, accessFromFile, rowNumber } = row;
  const { roles, groups } = catalog;

  const items = useMemo(() => {
    if (!access) return [];
    return [
      ...access.role_ids.map((id) => ({ key: `role:${id}`, label: roles.find((r) => r.id === id)?.name ?? id })),
      ...access.group_ids.map((id) => ({ key: `group:${id}`, label: groups.find((g) => g.id === id)?.name ?? id })),
    ];
  }, [access, roles, groups]);

  const handleRemove = useCallback(
    (key) => {
      const [kind, id] = key.split(':');
      const next = {
        ...access,
        role_ids: kind === 'role' ? access.role_ids.filter((x) => x !== id) : access.role_ids,
        group_ids: kind === 'group' ? access.group_ids.filter((x) => x !== id) : access.group_ids,
      };
      onChange(rowNumber, next);
    },
    [access, onChange, rowNumber],
  );

  if (row.status === ROW_STATUS.UPDATE) {
    return (
      <Badge size="small" variant="informative">
        {t('import.review.access.alreadyMember')}
      </Badge>
    );
  }

  // An Admin written in the file is access too, even with no roles or groups.
  const fileItems = [
    ...(fileUserType(accessFromFile.user_type) === 'admin' ? [t('terms.userType.admin')] : []),
    ...accessFromFile.roles,
    ...accessFromFile.groups,
  ];

  return (
    <div className="flex flex-col items-start gap-1 py-1">
      {access ? (
        <div className="flex flex-wrap items-center gap-1">
          <Badge size="small" variant={USER_TYPE_META[access.user_type].variant}>
            {t(USER_TYPE_META[access.user_type].labelKey)}
          </Badge>
          <RoleOverflowChips items={items} onRemove={handleRemove} />
        </div>
      ) : (
        <>
          <Badge size="small" variant="neutral">
            {t('import.review.access.noAccessYet')}
          </Badge>
          {fileItems.length > 0 ? (
            <span className="text-xs text-content-secondary">
              {t('import.review.access.inYourFile', { items: fileItems.join(', ') })}
            </span>
          ) : null}
        </>
      )}
      <AccessPopover row={row} roles={roles} groups={groups} onChange={onChange} />
    </div>
  );
}

export default memo(AccessCell);
